import type { SlotTimeFields } from './times';

/** The form values the request body is built from (all loosely typed). */
export interface SlotFormData {
  type?: unknown;
  bonuses?: {
    id?: number | undefined;
    amount?: unknown;
    business_order_count?: unknown;
  }[];
  cancel_order_count?: unknown;
  offer_reject_count?: unknown;
  returned_order_count?: unknown;
  equipment_ids?: unknown;
}

export interface SlotRequestBody extends SlotTimeFields {
  type: 'daily' | 'hourly';
  /** Blank limits are sent explicitly as `null` ("no limit"), not omitted. */
  offer_reject_count: number | null;
  cancelled_count: number | null;
  return_count: number | null;
  /** Empty means "all equipment". */
  equipment_ids: number[];
  bonuses: { id?: number; amount: number; business_order_count: number }[];
}

/**
 * Optional numeric limit: an unfilled field is an explicit `null` (the
 * backend's "no limit"); only a parseable value becomes a number. A
 * whitespace-only or non-numeric entry also collapses to `null`.
 */
export function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const raw = value.toString().trim();
  if (raw === '') return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Normalize the picked equipment into a clean `number[]`: option objects yield
 * their `id`, primitives are used as is, anything unparseable is dropped, and a
 * non-array reads as `[]` ("all equipment").
 */
export function normalizeEquipmentIds(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  const ids: number[] = [];
  for (const entry of value as unknown[]) {
    const id =
      entry !== null && typeof entry === 'object'
        ? (entry as { id?: unknown }).id
        : entry;
    if (typeof id !== 'string' && typeof id !== 'number') continue;
    const parsed = Number(id);
    if (!Number.isNaN(parsed)) ids.push(parsed);
  }
  return ids;
}

/**
 * Build the create/update body of a slot (cpanel `buildBonusPeriodPayload` /
 * `buildBonusPeriodUpdatePayload` — the same shape for both). A tier keeps its
 * `id` when it already exists, so an update edits it in place.
 */
export function buildSlotPayload(
  data: SlotFormData,
  times: SlotTimeFields,
): SlotRequestBody {
  return {
    type: data.type === 'hourly' ? 'hourly' : 'daily',
    ...times,
    offer_reject_count: toNullableNumber(data.offer_reject_count),
    cancelled_count: toNullableNumber(data.cancel_order_count),
    return_count: toNullableNumber(data.returned_order_count),
    equipment_ids: normalizeEquipmentIds(data.equipment_ids),
    bonuses: (data.bonuses ?? []).map((tier) => ({
      ...(tier.id === undefined ? {} : { id: tier.id }),
      amount: Number(tier.amount),
      business_order_count: Number(tier.business_order_count),
    })),
  };
}
